/**
 * Positions List Component
 * 
 * Displays user's positions across all markets
 * Read-only portfolio overview - click a position to navigate to MarketCard for trading
 */

import React from 'react';
import { UserMarketPosition } from '../../contexts/MarketProvider';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { displayQuestion, displayOptionTitleCase } from '../../utils/displayText';
import './PositionsList.css';

// Default image when market has no image
const DEFAULT_MARKET_IMAGE = '/assets/icons/imo-evergreen-floating.png';

interface PositionsListProps {
  positions: UserMarketPosition[];
  isLoading: boolean;
  onViewOpinion: (marketId: number) => void;
}

export const PositionsList: React.FC<PositionsListProps> = ({
  positions,
  isLoading,
  onViewOpinion,
}) => {
  
  // Format SOL amount
  const formatSol = (lamports: number): string => {
    return (lamports / LAMPORTS_PER_SOL).toFixed(2);
  };
  
  // Format time remaining or time since ended
  const formatTime = (endsAt: string): string => {
    const now = Date.now();
    const endTime = new Date(endsAt).getTime();
    const diff = endTime - now;
    
    if (diff > 0) {
      // Time remaining
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 24) {
        const days = Math.floor(hours / 24);
        return `${days}d left`;
      }
      return `${hours}h ${minutes}m left`;
    } else {
      // Time since ended
      const absDiff = Math.abs(diff);
      const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor(absDiff / (1000 * 60 * 60));
      
      if (days > 0) {
        return `${days}d ago`;
      }
      return `${hours}h ago`;
    }
  };
  
  // Get status emoji and label
  const getStatusDisplay = (position: UserMarketPosition) => {
    switch (position.status) {
      case 'ended_winner':
        return { emoji: '🏆', label: 'Winner' };
      case 'active':
        return { emoji: '🟢', label: 'Active' };
      case 'ended_loser':
        return { emoji: '❌', label: 'Lost' };
      case 'claimed':
        return { emoji: '✅', label: 'Claimed' };
      default:
        return { emoji: '⚪', label: 'Unknown' };
    }
  };
  
  // Calculate claim value for winners
  const getClaimValue = (position: UserMarketPosition): { sol: number; multiplier: number } | null => {
    if (position.status !== 'ended_winner' || !position.market.onChainState) {
      return null;
    }
    
    // Get winning option
    const winningOption = position.market.onChainState.options.find(
      opt => opt.optionIndex === position.winningOptionIndex
    );
    
    if (!winningOption) return null;
    
    // Calculate share of total pool
    const totalPool = position.market.onChainState.totalDeposits;
    const userTokenPercentage = position.position.tokens / winningOption.totalTokens;
    const claimValue = totalPool * userTokenPercentage;
    const multiplier = claimValue / position.position.costBasis;
    
    return {
      sol: claimValue / LAMPORTS_PER_SOL,
      multiplier
    };
  };
  
  if (isLoading) {
    return (
      <div className="market-list-loading">
        <div className="market-list-spinner" />
        <p>Loading your positions...</p>
      </div>
    );
  }
  
  if (positions.length === 0) {
    return (
      <div className="market-list-empty">
        <p>No positions yet</p>
        <span>Cast your first vote to see positions here</span>
      </div>
    );
  }
  
  // Filter out losing positions - user can't do anything with them (option is delisted on-chain)
  const visiblePositions = positions.filter(p => p.status !== 'ended_loser');
  
  if (visiblePositions.length === 0) {
    return (
      <div className="market-list-empty">
        <p>No positions yet</p>
        <span>Cast your first vote to see positions here</span>
      </div>
    );
  }
  
  return (
    <div className="market-list-grid">
      {visiblePositions.map((position) => {
        const status = getStatusDisplay(position);
        const optionLabel = position.market.options[position.position.optionIndex];
        const claimValue = getClaimValue(position);
        const marketImage = position.market.image_url || DEFAULT_MARKET_IMAGE;
        const isDefaultImage = !position.market.image_url;
        
        return (
          <div 
            key={`${position.market.id}-${position.position.user}`}
            className={`position-item position-${position.status}`}
            onClick={() => onViewOpinion(position.market.id)}
          >
            <div className="position-row">
              <div className={`market-list-item-image ${isDefaultImage ? 'default-image' : ''}`}>
                <img 
                  src={marketImage} 
                  alt="" 
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = DEFAULT_MARKET_IMAGE;
                    (e.target as HTMLImageElement).parentElement?.classList.add('default-image');
                  }}
                />
              </div>
              <div className="position-content">
                <div className="position-question-text">{displayQuestion(position.market.question)}</div>
                
                <div className="position-info">
                  <span className="position-option">{displayOptionTitleCase(optionLabel)}</span>
                  <span className="position-separator">•</span>
                  <span className="position-amount">{formatSol(position.position.costBasis)} SOL</span>
                </div>
                
                <div className="position-footer">
                  <span className="position-status-label">
                    {status.label}
                    {position.status === 'active' && ` · ${formatTime(position.market.ends_at)}`}
                  </span>
                  {claimValue && (
                    <span className="position-value">
                      ~{claimValue.sol.toFixed(2)} SOL ({claimValue.multiplier.toFixed(1)}x)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
